import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import _orderBy from 'lodash/orderBy';

import { baseApi, TAGS } from 'shared/api';
import type { BaseResponse } from 'shared/models/common';
import buildFilesFormData from 'shared/utils/buildFilesFormData';
import sleep from 'shared/utils/sleep';

import { fileLoaderApi, EntityType } from 'features/file-loader';

import type { BaseMassMessage, MassMessageDocument } from './types';

const sortMassMessageDocuments = (documents: MassMessageDocument[]) =>
  _orderBy(documents, (document) => document.Name.toLocaleLowerCase(), 'asc');

export const massMessagesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMessagesList: build.query<BaseMassMessage[], string>({
      query: (cooperativeId) => ({
        url: `/user/cooperatives/${cooperativeId}/mass-messages`,
        method: 'GET',
      }),
      transformResponse: (response: BaseResponse<BaseMassMessage[]>) =>
        response.Data.map((massMessage) => ({
          ...massMessage,
          Documents: sortMassMessageDocuments(massMessage.Documents),
        })),
      providesTags: (_res, err) => (err ? [] : [TAGS.MASS_MESSAGES]),
    }),
    saveMassMessage: build.mutation<
      BaseMassMessage,
      {
        body: Record<string, any>;
        coopId: string;
        documentsToBeDeleted: string[];
        files: File[];
        variant: 'create' | 'update';
      }
    >({
      queryFn: async (
        { body, coopId, files, documentsToBeDeleted, variant },
        { dispatch },
        _extra,
        fetchWithBQ
      ) => {
        const createMassMessageResult = await fetchWithBQ({
          url: `/user/cooperatives/${coopId}/mass-messages/${variant}`,
          method: variant === 'create' ? 'POST' : 'PUT',
          body,
        });

        if (createMassMessageResult.error) {
          return { error: createMassMessageResult.error };
        }

        const { Data: newMassMessage } =
          createMassMessageResult.data as BaseResponse<BaseMassMessage>;

        if (documentsToBeDeleted.length) {
          await Promise.all(
            documentsToBeDeleted.map((id) =>
              dispatch(
                fileLoaderApi.endpoints.deleteDocument.initiate({
                  cooperativeId: coopId,
                  entityId: newMassMessage.Id,
                  entityType: EntityType.MassMessage,
                  fileUrl: id,
                })
              )
            )
          );
        }

        if (!!files.length) {
          const uploadMassMessageFilesResult = await fetchWithBQ({
            url: `/user/cooperatives/${coopId}/documents/uds_communicationmessage/${newMassMessage.Id}`,
            method: 'POST',
            body: buildFilesFormData(files),
          });

          if (uploadMassMessageFilesResult.error) {
            const error = (
              uploadMassMessageFilesResult.error as FetchBaseQueryError
            ).data as { Message?: string };
            const warning = `Selected files were not uploaded: ${error?.Message}`;
            console.warn(warning);
          }
        }

        await sleep(3000);

        return { data: newMassMessage };
      },
      invalidatesTags: (_, error) => (error ? [] : [TAGS.MASS_MESSAGES]),
    }),
  }),
});
